import { InputTextarea } from "primereact/inputtextarea";
import styled from "styled-components";

import { previewTheme } from "../preview";

/**
 * Compact by default — the stock disclaimer text runs to several paragraphs,
 * so an auto-growing field would push everything else off screen. The drag
 * handle stays available for anyone who wants to see more at once.
 */
export const StyledDisclaimerTextarea = styled(InputTextarea)`
  && {
    resize: vertical;
    min-height: 80px;
    max-height: 60vh;
  }
`;

/**
 * Settings beside the page preview on wide screens. Below the breakpoint the
 * grid collapses to one column, so source order (settings, then preview) is
 * also the stacked order, with the disclaimer card following outside the grid.
 */
export const StyledPreviewColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: start;
  margin-bottom: 24px;

  @media (min-width: 1200px) {
    /* Proportional, not capped, so the pair always fills the shell width. */
    grid-template-columns: minmax(520px, 2fr) minmax(0, 3fr);
  }
`;

export const StyledSettingsCard = styled.div`
  background-color: ${previewTheme.surface};
  border: 1px solid ${previewTheme.hairline};
  border-radius: 16px;
  padding: 24px;
`;

export const StyledSettingsHeader = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${previewTheme.ink};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  margin: 0 0 8px;

  i {
    color: ${previewTheme.indigo};
    font-size: 16px;
  }
`;

export const StyledSettingsDescription = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 8px;
`;

export const StyledSettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 0;
  border-top: 1px solid ${previewTheme.hairline};

  &:first-of-type {
    border-top: none;
  }
`;

export const StyledSettingTitle = styled.p`
  && {
    color: ${previewTheme.ink};
  }
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0 0 2px;
`;

export const StyledSettingSubtitle = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
`;

/* Mirrors the real checkout page (OrderOverview Overview/Home). */
export const StyledCheckoutPageCard = styled.div`
  background-color: ${previewTheme.surface};
  border: 1px solid ${previewTheme.hairline};
  border-radius: 16px;
  padding: 32px;

  p {
    color: ${previewTheme.body};
  }
`;

export const StyledSectionLabel = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin: 0 0 8px;
`;

export const StyledSummaryContainer = styled.div`
  position: relative;
  border: 1px solid ${previewTheme.hairline};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

export const StyledServiceDate = styled.p`
  && {
    color: ${previewTheme.ink};
  }
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  margin: 8px 0 4px;
`;

export const StyledServiceName = styled.p`
  && {
    color: ${previewTheme.ink};
  }
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  margin: 0 0 2px;
`;

export const StyledServiceMeta = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  margin: 0 0 10px;
`;

export const StyledProviderLine = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 14px;
  line-height: 1.7;
  margin: 0;
`;

export const StyledSummaryColumns = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;

  > div:first-child {
    flex: 1;
  }
`;

export const StyledAmountColumn = styled.div`
  min-width: 160px;
  text-align: right;
`;

export const StyledAmount = styled.p`
  && {
    color: ${previewTheme.ink};
  }
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  margin: 0;
`;

export const StyledStaticCaption = styled.p`
  && {
    color: ${previewTheme.muted};
  }
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
`;

/**
 * Static replica of the patient page's Pay in Full / Payment Plan selector.
 * Deliberately not interactive — the preview always shows the Pay in Full
 * state, which is the branch the customizable fields below belong to.
 */
export const StyledPaymentToggle = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid #e3e1f7;
  background-color: #f8f8fd;
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 24px;
  user-select: none;
`;

export const StyledToggleOption = styled.div<{ $active?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  background-color: ${({ $active }) =>
    $active ? previewTheme.indigo : "transparent"};
  color: ${({ $active }) =>
    $active ? previewTheme.surface : previewTheme.muted};
`;

export const StyledSavePill = styled.span`
  background-color: #e4f7ea;
  color: ${previewTheme.green};
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 600;
`;

export const StyledPriceContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  border: 1px solid ${previewTheme.indigoBorder};
  border-radius: 12px;
  padding: 20px;
  background-color: #f6f8fe;

  > div:first-child {
    flex: 1;
  }
`;

export const StyledPrice = styled.p`
  && {
    color: ${previewTheme.indigo};
  }
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`;

/* Text-only trigger matching the patient page's "Why do I owe this amount?". */
export const StyledLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  color: ${previewTheme.indigo};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }

  i {
    font-size: 12px;
    transition: transform 0.3s ease-in-out;
  }

  i.rotate {
    transform: rotate(180deg);
  }
`;

export const StyledDetailsPanel = styled.div`
  border: 1px dashed ${previewTheme.hairline};
  border-radius: 10px;
  padding: 16px;
  margin-top: 12px;
`;

export const StyledPlaceholderNote = styled.p`
  && {
    color: ${previewTheme.muted};
  }
  font-size: 13px;
  font-style: italic;
  line-height: 1.6;
  margin: 0 0 12px;
`;

export const StyledDiscountNote = styled.p`
  && {
    color: ${previewTheme.green};
  }
  font-size: 12px;
  line-height: 1.4;
  margin: 4px 0 0;
`;

export const StyledStrikePrice = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 12px;
  line-height: 1.4;
  margin: 2px 0 0;

  span {
    text-decoration: line-through;
  }
`;

export const StyledExpiryNote = styled.p`
  && {
    color: ${previewTheme.red};
  }
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
  margin: 16px 0 0;
`;
